import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from './utils/firebase';
import { applyActionCode } from 'firebase/auth';
import CircularProgress from '@mui/material/CircularProgress';
import { CircleCheck, CircleX } from 'lucide-react';
import logo from './assets/featherglass-sq-logo.png';
import { companyName } from './globals';
import {
  consumeEmailAction,
  resolveEmailActionSignInUrl,
  resolveVerificationReturnUrl,
  sanitizeAuthActionError,
  stashPasswordResetCode,
} from './utils/emailVerification';

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('');
  const hasRun = useRef(false); // Add this

  useEffect(() => {
    if (hasRun.current) return; // Prevent double execution
    hasRun.current = true; // Mark as run
    
    const handleAuthAction = async () => {
      const { mode, actionCode, continueUrl, isSynthetic } = consumeEmailAction({ searchParams });
      const verificationReturnUrl = resolveVerificationReturnUrl(continueUrl);
      const returnSignInUrl = resolveEmailActionSignInUrl(continueUrl);

      const redirectTo = (targetUrl) => {
        const target = new URL(targetUrl);
        if (target.origin === window.location.origin) {
          if (isSynthetic) {
            navigate(`${target.pathname}${target.search}${target.hash}`, { replace: true });
          } else {
            navigate(`${target.pathname}${target.search}${target.hash}`);
          }
        } else {
          window.location.assign(target.toString());
        }
      };

      if (!mode || !actionCode) {
        setStatus('error');
        setMessage('Invalid verification link.');
        setTimeout(() => redirectTo(returnSignInUrl), 3000);
        return;
      }

      try {
        switch (mode) {
          case 'verifyEmail':
            await applyActionCode(auth, actionCode);
            setStatus('success');
            setMessage('');
            // Return to the environment where signup began after 3 seconds.
            setTimeout(() => {
              redirectTo(verificationReturnUrl);
            }, 3000);
            break;

          case 'resetPassword':
            if (isSynthetic) {
              stashPasswordResetCode(actionCode);
              navigate('/reset-password', { replace: true });
            } else {
              // Handle password reset - redirect to password reset page with code
              navigate(`/reset-password?oobCode=${actionCode}`);
            }
            break;

          case 'recoverEmail':
            // Handle email recovery
            await applyActionCode(auth, actionCode);
            setStatus('success');
            setMessage('Email recovered successfully!');
            setTimeout(() => redirectTo(returnSignInUrl), 3000);
            break;

          default:
            setStatus('error');
            setMessage('Unknown action type.');
            setTimeout(() => redirectTo(returnSignInUrl), 3000);
        }
      } catch (error) {
        console.error('Error handling auth action:', sanitizeAuthActionError(error));
        setStatus('error');
        
        if (error.code === 'auth/invalid-action-code') {
          setMessage('This verification link is invalid or has expired. Please request a new one.');
        } else if (error.code === 'auth/expired-action-code') {
          setMessage('This verification link has expired. Please request a new one.');
        } else {
          setMessage('An error occurred. Please try again.');
        }
        
        setTimeout(() => redirectTo(returnSignInUrl), 5000);
      }
    };

    handleAuthAction();
  }, [searchParams, navigate]);

  return (
    <div className="login-container">
      <img src={logo} alt="Logo" className="logo-login" />
      
      <div className="verification-notice" style={{ marginTop: '2rem' }}>
        {status === 'processing' && (
          <>
            <CircularProgress size={48} style={{ marginBottom: 16 }} />
            <h3>Verifying your email...</h3>
            <p>Please wait while we confirm your email address.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CircleCheck style={{ color: 'var(--color-success)', marginBottom: 16 }} size={48} />
            <h3>Email Verified!</h3>
            {message && <p>{message}</p>}
            <p style={{ marginTop: 16, fontSize: 'var(--text-sm)', color: 'var(--color-text-light)' }}>
              Redirecting you to sign in...
            </p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <CircleX style={{ color: 'var(--color-error)', marginBottom: 16 }} size={48} />
            <h3>Verification Failed</h3>
            <p>{message}</p>
            <p style={{ marginTop: 16, fontSize: 'var(--text-sm)', color: 'var(--color-text-light)' }}>
              Redirecting you to sign in...
            </p>
          </>
        )}
      </div>

      <footer className="footer" style={{ marginTop: '2rem' }}>
        <p>&copy; {new Date().getFullYear()} {companyName}. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default AuthAction;
